import type { AdminSessionResponse, AdminUser } from "@contracts/admin";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { apiRequest, jsonBody, loadSession, setCsrfToken } from "@frontend/api/client";

type AuthState = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  state: AuthState;
  user: AdminUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>("loading");
  const [user, setUser] = useState<AdminUser | null>(null);

  const refresh = useCallback(async () => {
    const session = await loadSession();
    setUser(session?.user ?? null);
    setState(session ? "authenticated" : "anonymous");
  }, []);

  useEffect(() => {
    void refresh().catch(() => {
      setUser(null);
      setState("anonymous");
    });
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const session = await apiRequest<AdminSessionResponse>("/api/v1/auth/login", {
      method: "POST",
      body: jsonBody({ email, password }),
    });
    setCsrfToken(session.csrf_token);
    setUser(session.user);
    setState("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await apiRequest<{ logged_out: true }>("/api/v1/auth/logout", { method: "POST" });
    setCsrfToken(null);
    setUser(null);
    setState("anonymous");
  }, []);

  const value = useMemo(
    () => ({ state, user, login, logout, refresh }),
    [state, user, login, logout, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider.");
  }
  return value;
}
